import { useAppTranslation } from "@/utils";
import { Divider, Flex, Grid, Progress, Steps, Typography } from "@payping/web-components";
import classNames from "classnames";
import { FC } from "react";
import { BnplPlanStepEnum } from "./types";

interface StepsProps {
  currentStep: BnplPlanStepEnum;
  showSalaryStep?: boolean;
}
type StepDotProps = {
  isActive: boolean;
};
const BnplManagePlanPageSteps: FC<StepsProps> = ({ currentStep, showSalaryStep }) => {
  const { t } = useAppTranslation();
  const { xl } = Grid.useBreakpoint();
  const StepDot: FC<StepDotProps> = ({ isActive }) => (
    <span className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center">
      <span
        className={`${isActive ? "tw-bg-color-primary" : "tw-bg-color-text-placeholder"} tw-inline-block tw-h-2 tw-w-2 tw-rounded-full`}
      />
    </span>
  );

  const items = [
    {
      id: BnplPlanStepEnum.PlanAmount,
      title: t("shop.bnpl.addInstallmentPlan.steps.setPlanAmount.title"),
      description: (
        <Typography.Text type={"secondary"}>
          {t("shop.bnpl.addInstallmentPlan.steps.setPlanAmount.description")}
        </Typography.Text>
      ),
      icon: <StepDot isActive={currentStep > BnplPlanStepEnum.PlanAmount} />,
    },
    {
      id: BnplPlanStepEnum.PreInstallmentAmount,
      title: t("shop.bnpl.addInstallmentPlan.steps.PreInstallmentAmount.title"),
      description: t("shop.bnpl.addInstallmentPlan.steps.PreInstallmentAmount.description"),
      icon: <StepDot isActive={currentStep > BnplPlanStepEnum.PreInstallmentAmount} />,
    },
    {
      id: BnplPlanStepEnum.InstallmentCount,
      title: t("shop.bnpl.addInstallmentPlan.steps.InstallmentCount.title"),
      description: t("shop.bnpl.addInstallmentPlan.steps.InstallmentCount.description"),
      icon: <StepDot isActive={currentStep > BnplPlanStepEnum.InstallmentCount} />,
    },
    {
      id: BnplPlanStepEnum.ProfitPercent,
      title: t("shop.bnpl.addInstallmentPlan.steps.ProfitPercent.title"),
      description: t("shop.bnpl.addInstallmentPlan.steps.ProfitPercent.description"),
      icon: <StepDot isActive={currentStep > BnplPlanStepEnum.ProfitPercent} />,
    },
    {
      id: BnplPlanStepEnum.PlanContract,
      title: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.title"),
      description: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.description"),
      icon: <StepDot isActive={currentStep > BnplPlanStepEnum.PlanContract} />,
    },
  ];

  if (showSalaryStep) {
    items.push({
      id: BnplPlanStepEnum.SalaryDeduction,
      title: t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.title"),
      description: t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.description"),
      icon: <StepDot isActive={currentStep > BnplPlanStepEnum.PlanContract} />,
    });
  }

  const listLength = items.length * 60;

  return (
    <>
      <Flex
        className={classNames({
          "tw-min-w-[calc(272px+4rem)]": xl,
          "tw-w-full": !xl,
        })}
        vertical={!xl}
      >
        <Flex vertical={!xl} gap={"large"} className="tw-w-full tw-p-4 xl:tw-p-8">
          {xl ? (
            <Steps
              className="tw-w-full"
              style={{
                height: `${listLength}px`,
              }}
              status="process"
              responsive
              size="small"
              current={currentStep}
              direction="vertical"
              items={items}
            />
          ) : (
            <>
              <Typography.Title level={5} className="!tw-m-0">
                {items[currentStep]?.title}
              </Typography.Title>
              <Progress
                status={"active"}
                percent={((currentStep + 1) / items.length) * 100}
                size="default"
                format={() => (
                  <div>
                    {t("shop.bnpl.activation.formDrawer.steps.currentLevel", {
                      current: currentStep + 1,
                      total: items.length,
                    })}
                  </div>
                )}
              />
            </>
          )}
        </Flex>
        <Divider
          className={classNames([
            "!tw-m-0 tw-mt-4",
            {
              "tw-h-full": xl,
              "tw-w-full": !xl,
            },
          ])}
          type={xl ? "vertical" : "horizontal"}
        />
      </Flex>
    </>
  );
};

export default BnplManagePlanPageSteps;
